import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../utils/api.js";
import { URLS, ImgURL } from "../utils/urls.js";
import AdminMediaGallery from "./AdminMediaGallery.jsx";

const AddSong = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // song id for edit
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [form, setForm] = useState({
    title: "",
    artist: "",
    platform: "spotify",
    song_url: "",
    cover_image_url: "",
    personality_codes: []
  });
  const [personalities, setPersonalities] = useState([]);

  useEffect(() => {
    fetchPersonalities();
    if (isEditing) {
      fetchSong();
    }
  }, [id]);

  const fetchPersonalities = async () => {
    try {
      const res = await api.get(URLS.GET_PERSONALITY_LIST);
      const allowedCodes = ["NUR", "GOG", "SOC", "DPS", "SLC", "EXP", "VIS", "GRD"];
      const filtered = res.data.filter(p => allowedCodes.includes(p.code));
      setPersonalities(filtered);
    } catch (err) {
      console.error("Error loading personalities:", err);
    }
  };

  const fetchSong = async () => {
    try {
      const res = await api.get(URLS.GET_SONG(id));
      setForm({
        title: res.data.title || "",
        artist: res.data.artist || "",
        platform: res.data.platform || "spotify",
        song_url: res.data.song_url || "",
        cover_image_url: res.data.cover_image_url || "",
        personality_codes: res.data.personality_codes || []
      });
    } catch (err) {
      console.error("Error loading song:", err);
      alert("Unable to load song");
      navigate("/admin/playlist");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const togglePersonality = (code) => {
    setForm(prev => {
        const codes = prev.personality_codes.includes(code)
            ? prev.personality_codes.filter(c => c !== code)
            : [...prev.personality_codes, code];
        return { ...prev, personality_codes: codes };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title || !form.artist || !form.song_url) {
      alert("Title, Artist, and Song URL are required");
      return;
    }

    try {
      setSaving(true);

      if (isEditing) {
        await api.put(URLS.UPDATE_SONG(id), form);
      } else {
        await api.post(URLS.CREATE_SONG, form);
      }

      navigate("/admin/playlist");
    } catch (err) {
      console.error("Error saving song:", err);
      alert("Failed to save song");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center">Loading...</div>;
  }

  return (
    <div className="p-6 min-h-screen">
      <h1 className="text-3xl font-semibold mb-6">
        {isEditing ? "Edit Song" : "Add Song"}
      </h1>

      <form
        onSubmit={handleSubmit}
        className="max-w-xl bg-white p-6 rounded-lg shadow border"
      >
        {/* Song Title */}
        <div className="mb-4">
          <label className="block mb-1 font-medium">Song Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => updateField("title", e.target.value)}
            className="w-full border rounded-md p-2"
            required
          />
        </div>

        {/* Artist */}
        <div className="mb-4">
          <label className="block mb-1 font-medium">Artist *</label>
          <input
            type="text"
            value={form.artist}
            onChange={(e) => updateField("artist", e.target.value)}
            className="w-full border rounded-md p-2"
            required
          />
        </div>

        {/* Platform */}
        <div className="mb-4">
          <label className="block mb-1 font-medium">Platform *</label>
          <select
            value={form.platform}
            onChange={(e) => updateField("platform", e.target.value)}
            className="w-full border rounded-md p-2"
          >
            <option value="spotify">Spotify</option>
            <option value="apple_music">Apple Music</option>
            <option value="youtube">YouTube</option>
            <option value="linktree">Linktree</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Song URL */}
        <div className="mb-4">
          <label className="block mb-1 font-medium">Song URL *</label>
          <input
            type="url"
            value={form.song_url}
            onChange={(e) => updateField("song_url", e.target.value)}
            className="w-full border rounded-md p-2"
            required
          />
        </div>

        {/* Personality Types */}
        <div className="mb-6">
          <label className="block mb-2 font-medium">Associated Personalities</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {personalities.map((p) => (
              <label key={p.code} className="flex items-center gap-2 p-2 border rounded-md hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.personality_codes.includes(p.code)}
                  onChange={() => togglePersonality(p.code)}
                  className="rounded text-indigo-600"
                />
                <span className="text-sm truncate" title={p.title}>{p.title}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Cover Image */}
        <div className="mb-6">
          <label className="block mb-2 font-medium">Song Cover Image</label>
          <div className="flex items-center gap-4">
            {form.cover_image_url ? (
              <img
                src={ImgURL(form.cover_image_url)}
                alt="Cover"
                className="w-20 h-20 object-cover rounded-md border"
              />
            ) : (
              <div className="w-20 h-20 bg-gray-100 rounded-md border flex items-center justify-center text-gray-400 text-xs text-center p-2">
                No Image
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowGallery(true)}
              className="px-4 py-2 bg-gray-800 text-white text-sm rounded-md hover:bg-gray-900 transition"
            >
              {form.cover_image_url ? "Change Image" : "Select Image"}
            </button>
            {form.cover_image_url && (
                <button
                    type="button"
                    onClick={() => updateField("cover_image_url", "")}
                    className="text-red-500 text-sm hover:underline"
                >
                    Remove
                </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition disabled:opacity-50"
          >
            {saving
              ? "Saving..."
              : isEditing
              ? "Update Song"
              : "Save Song"}
          </button>

          <button
            type="button"
            onClick={() => navigate("/admin/playlist")}
            className="px-4 py-2 border rounded-md hover:bg-gray-50 transition"
          >
            Cancel
          </button>
        </div>
      </form>

      {/* MEDIA GALLERY MODAL */}
      {showGallery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white w-11/12 max-w-5xl p-4 rounded-lg shadow-lg relative">
            <button
              className="absolute top-3 right-3 text-gray-600 hover:text-black font-bold"
              type="button"
              onClick={() => setShowGallery(false)}
            >
              ✕
            </button>

            <h3 className="text-xl font-semibold mb-4">Select Song Cover</h3>

            <AdminMediaGallery
              accept="image/*"
              onSelect={(m) => {
                updateField("cover_image_url", m.url);
                setShowGallery(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AddSong;
